import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, UserCircle } from 'lucide-react';
import { loadImage } from '@fias/canvas-kit';
import {
  AVATAR_FULL_SIZE_PX,
  drawAvatarLayers,
  getAccessoryIdentifier,
  planAvatarLayers,
  type LoadedAvatarLayer,
} from '@fias/litsquad-avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/auth/hooks/useAuth';
import type { LitSquadInventoryEntry } from '@/lib/api';
import {
  getLitSquadSpecies,
  isSafeImageUri,
  keyOutWhiteBackground,
  resolveLitSquadThumbnail,
} from '../utils';
import type { UseAvatarEditorStateReturn } from '../hooks/useAvatarEditorState';
import type { UseLitSquadAvatarRecipeReturn } from '../hooks/useLitSquadAvatarRecipe';
import { AvatarEditorAccessoryChip } from './AvatarEditorAccessoryChip';

const CDN_BASE_URL: string = import.meta.env.VITE_DATA_STORAGE_CDN_URL ?? '';

export interface AvatarEditorProps {
  /**
   * Editor state — owned by the parent so the inventory grid's "Set as
   * avatar" / "Add accessory" buttons can drive the same state.
   */
  editor: UseAvatarEditorStateReturn;
  /**
   * Saves the avatar. The server renders the images from the recipe with the
   * same `@fias/litsquad-avatar` geometry this preview draws with.
   */
  recipe: Pick<UseLitSquadAvatarRecipeReturn, 'save' | 'saving' | 'error' | 'savedVersion'>;
  /**
   * The user's owned items. Source of truth for resolving image URIs from
   * the IDs the editor state holds. Pass the same list rendered in the
   * inventory grid so adding from the grid lights up immediately.
   */
  items: readonly LitSquadInventoryEntry[];
}

export function AvatarEditor({ editor, recipe, items }: AvatarEditorProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const username = user?.username ?? null;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  /** The `uriKey` the canvas last finished drawing — Save is held until it is the current one. */
  const [drawnKey, setDrawnKey] = useState<string | null>(null);

  const itemsByItemId = useMemo(() => {
    const map = new Map<string, LitSquadInventoryEntry>();
    for (const entry of items) {
      if (entry.itemId) map.set(entry.itemId, entry);
    }
    return map;
  }, [items]);

  const baseEntry = editor.baseItemId ? (itemsByItemId.get(editor.baseItemId) ?? null) : null;

  // Resolve URIs eagerly. Computing inside the redraw effect would require
  // adding entry references to the dep array, and those reidentify
  // whenever the parent's items list changes (e.g. paginated load), even
  // when the chosen IDs are unchanged. Joining the IDs into a stable
  // string is the dep we actually want.
  const baseUri = useMemo(
    () => (baseEntry ? resolveLitSquadThumbnail(baseEntry) : null),
    [baseEntry],
  );
  /**
   * Base character species, derived once — by the server's own rule, so the
   * preview places accessories where the saved avatar will have them. The
   * transform lookup for every accessory below depends on this: when the
   * user swaps the base character to a different species, every accessory's
   * transform flips, so this is the right cache key.
   */
  const baseSpecies = useMemo(
    () => (baseEntry ? getLitSquadSpecies(baseEntry) : null),
    [baseEntry],
  );
  /**
   * The accessories to draw, in the editor's order, as their item id, art
   * URI and placement identifier. Entries no longer in the inventory list
   * (rare; pagination edge case) or with no safe art are left out — and Save
   * sends exactly these, so the saved avatar is the one previewed.
   */
  const accessorySources = useMemo(() => {
    const drawable: { itemId: string; artUri: string; identifier: string | null }[] = [];
    for (const itemId of editor.accessoryItemIds) {
      const entry = itemsByItemId.get(itemId);
      const artUri = entry ? resolveLitSquadThumbnail(entry) : null;
      if (!entry || !artUri || !isSafeImageUri(artUri)) continue;
      drawable.push({ itemId, artUri, identifier: getAccessoryIdentifier(entry.itemMetadata) });
    }
    return drawable;
  }, [editor.accessoryItemIds, itemsByItemId]);
  const undrawnAccessories = editor.accessoryItemIds.length - accessorySources.length;
  /**
   * Stable redraw cache key built from primitive editor state. We
   * deliberately do NOT include `accessorySources` (its identity
   * churns on every items-list pagination tick); transforms are
   * deterministic from `(baseSpecies, accessoryItemIds)` so swapping
   * either is enough to invalidate the cache.
   */
  const uriKey = useMemo(
    () =>
      `${editor.baseItemId ?? ''}|${baseUri ?? ''}|${baseSpecies ?? ''}|${editor.accessoryItemIds.join(',')}`,
    [editor.baseItemId, baseUri, baseSpecies, editor.accessoryItemIds],
  );

  // Keep the source-of-truth accessory entries for chip rendering.
  const accessoryEntries = useMemo(
    () =>
      editor.accessoryItemIds
        .map((id) => itemsByItemId.get(id))
        .filter((e): e is LitSquadInventoryEntry => e !== undefined),
    [editor.accessoryItemIds, itemsByItemId],
  );

  // Compose the preview canvas whenever the IDs (not array references) change.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;
    setDrawError(null);

    ctx.clearRect(0, 0, AVATAR_FULL_SIZE_PX, AVATAR_FULL_SIZE_PX);
    if (!baseUri || !isSafeImageUri(baseUri)) {
      setDrawing(false);
      return;
    }

    setDrawing(true);
    // The same layer plan and draw rules the server renders the saved
    // avatar with — this is a preview of exactly what Save produces.
    const plan = planAvatarLayers({
      species: baseSpecies,
      base: { artUri: baseUri },
      accessories: accessorySources,
    });
    // ⚠ Lambda, not point-free `.map(loadImage)` — map's index argument
    // would land in loadImage's options parameter.
    Promise.all(plan.map((layer) => loadImage(layer.artUri, { crossOrigin: 'anonymous' })))
      .then((images) => {
        if (cancelled) return;
        const layers: LoadedAvatarLayer<CanvasImageSource>[] = images.map((img, i) => ({
          // Legacy accessory art is JPEG on white; key the white out so it
          // doesn't cover the character beneath.
          image: keyOutWhiteBackground(img),
          width: img.naturalWidth,
          height: img.naturalHeight,
          transform: plan[i].transform,
        }));
        ctx.clearRect(0, 0, AVATAR_FULL_SIZE_PX, AVATAR_FULL_SIZE_PX);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        drawAvatarLayers(ctx, layers, AVATAR_FULL_SIZE_PX);
        setDrawing(false);
        setDrawnKey(uriKey);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to compose preview';
        setDrawError(msg);
        setDrawing(false);
      });

    return () => {
      cancelled = true;
    };
    // Intentional: dep is the joined uriKey rather than the
    // baseUri/accessorySources references. The references reidentify
    // whenever the parent's items list paginates, but uriKey only
    // changes when the underlying URIs/transforms do — which is what
    // we want.
  }, [uriKey]);

  const handleSave = useCallback(async () => {
    if (!username) {
      toast({
        title: 'Sign-in required',
        description: 'You need to be signed in to save an avatar.',
        variant: 'destructive',
      });
      return;
    }
    if (!editor.baseItemId) return;

    // Exactly the accessories the preview drew, so the saved avatar is the
    // one on screen.
    const accessoryItemIds = accessorySources.map((source) => source.itemId);
    try {
      // The server renders and stores the avatar from the recipe, so a saved
      // avatar is the same whether this editor or a plugin set it.
      await recipe.save({ baseItemId: editor.baseItemId, accessoryItemIds });
      editor.resetToSaved({ baseItemId: editor.baseItemId, accessoryItemIds });
      toast({ title: 'Avatar saved', description: 'Your new avatar is live.' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save avatar';
      toast({ title: 'Save failed', description: msg, variant: 'destructive' });
    }
  }, [username, editor, recipe, toast, accessorySources]);

  const handleCancel = useCallback(() => {
    editor.cancel();
  }, [editor]);

  const savedAvatarUrl = useMemo(() => {
    if (!username || !CDN_BASE_URL) return null;
    return `${CDN_BASE_URL.replace(/\/$/, '')}/dfe-avatars/Avatars/${encodeURIComponent(
      username,
    )}.png?v=${recipe.savedVersion}`;
  }, [username, recipe.savedVersion]);

  // Save is pointless while not dirty or while a previous save is in
  // flight, and held until the preview of the current choice has drawn — not
  // while it loads, and not after it failed — so the player saves only what
  // they have actually seen.
  const isSaveDisabled =
    !editor.baseItemId || !editor.isDirty || recipe.saving || drawnKey !== uriKey;

  return (
    <Card className="overflow-hidden">
      <CardContent className="flex flex-col gap-4 p-4 md:flex-row">
        <div className="flex flex-col items-center gap-2">
          <div className="relative h-64 w-64 overflow-hidden rounded-md border bg-muted md:h-72 md:w-72">
            <canvas
              ref={canvasRef}
              width={AVATAR_FULL_SIZE_PX}
              height={AVATAR_FULL_SIZE_PX}
              className="h-full w-full"
              data-testid="avatar-editor-canvas"
            />
            {!editor.baseItemId && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                <UserCircle className="h-12 w-12" />
                <span>Pick a LitSquad character below to start</span>
              </div>
            )}
            {drawing && editor.baseItemId && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-background/40"
                data-testid="avatar-editor-loading"
              >
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {drawError && (
              <div className="absolute inset-x-0 bottom-0 bg-destructive/90 px-2 py-1 text-center text-xs text-destructive-foreground">
                {drawError}
              </div>
            )}
          </div>
          {undrawnAccessories > 0 && (
            <p
              className="w-64 text-center text-xs text-muted-foreground md:w-72"
              data-testid="avatar-editor-undrawn"
            >
              {undrawnAccessories === 1
                ? "1 accessory has no art to show, so it won't be saved."
                : `${undrawnAccessories} accessories have no art to show, so they won't be saved.`}
            </p>
          )}
          <div className="flex w-full gap-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaveDisabled}
              aria-label="Save avatar"
              className="flex-1"
            >
              {recipe.saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={!editor.isDirty || recipe.saving}
              aria-label="Discard avatar changes"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div>
            <h2 className="text-base font-semibold">Your avatar</h2>
            <p className="text-xs text-muted-foreground">
              {username ? (
                <>
                  Saved as <code className="font-mono">{username}</code>
                </>
              ) : (
                'Sign in to save an avatar'
              )}
            </p>
          </div>

          {accessoryEntries.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {accessoryEntries.map((entry) => (
                <AvatarEditorAccessoryChip
                  key={entry.itemId ?? entry.itemDefId}
                  entry={entry}
                  onRemove={editor.removeAccessory}
                />
              ))}
            </div>
          )}

          {savedAvatarUrl && (
            <div className="mt-auto flex items-center gap-3 border-t pt-3 text-xs text-muted-foreground">
              <span>Currently saved:</span>
              <img
                src={savedAvatarUrl}
                alt="Currently saved avatar"
                className="h-12 w-12 rounded-full border bg-muted object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
            </div>
          )}

          {recipe.error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 px-2 py-1 text-xs text-destructive">
              {recipe.error}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
